import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import blogData from '../data/blogArticles.json';

const Blog = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);
  const [thumbStyle, setThumbStyle] = React.useState({ width: '0%', transform: 'translateX(0%)' });

  useEffect(() => {
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          sectionRef.current.classList.add('visible');
          observer.disconnect();
        }
      }, { threshold: 0.2 });
      
      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }
      
      return () => observer.disconnect();
    }
  }, []);

  // Indicateur de défilement pour le carousel mobile
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        setThumbStyle({ width: '100%', transform: 'translateX(0%)' });
        return;
      }
      const progress = el.scrollLeft / max;
      const widthPercent = Math.max(20, (el.clientWidth / el.scrollWidth) * 100);
      const travel = 100 - widthPercent;
      const translate = travel * progress;
      setThumbStyle({ width: widthPercent + '%', transform: `translateX(${translate}%)` });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      locale: 'fr-FR'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <section className="blog section-animate" id="blog" ref={sectionRef}>
      <div className="container">
        <div className="section-header">
          <h2 className="section-title title-animate">{t('blog.title')}</h2>
          <p className="section-subtitle subtitle-animate">{t('blog.description')}</p>
        </div>

        <div className="blog-grid" ref={carouselRef}>
          {blogData.articles.map((article, index) => (
            <article
              key={article.id}
              className="blog-card stagger-item card-premium focus-premium"
              tabIndex="0"
              style={{ '--stagger-delay': `${index * 100}ms` }}
            >
              <div className="blog-image-container">
                <img
                  className="blog-image"
                  src={article.image}
                  alt={article.title}
                  loading="lazy"
                />
                <div className="blog-category-badge">
                  {article.category}
                </div>
                <div className="blog-overlay">
                  <span className="blog-read-time">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z"/>
                    </svg>
                    {article.readTime}
                  </span>
                </div>
              </div>
              
              <div className="blog-content">
                <div className="blog-meta">
                  <time dateTime={article.date} className="blog-date">
                    {formatDate(article.date)}
                  </time>
                  <span className="blog-author-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                    </svg>
                    {article.author}
                  </span>
                </div>
                
                <h3 className="blog-title">{article.title}</h3>
                
                <p className="blog-summary">{article.summary}</p>
                
                <div className="blog-footer">
                  <Link 
                    to={`/blog/${article.slug}`}
                    className="blog-read-more btn btn-secondary btn-sm btn-ripple focus-premium"
                  >
                    {t('blog.readMore')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"/>
                    </svg>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
        {/* Indicateur de défilement (mobile) */}
        <div className="blog-carousel-indicator">
          <div className="blog-carousel-track">
            <div className="blog-carousel-thumb" style={thumbStyle} />
          </div>
        </div>
        
        <div className="blog-cta">
          <p className="blog-cta-text">Besoin de conseils personnalisés pour votre projet énergétique ?</p>
          <a href="#contact" className="btn btn-primary btn-md btn-ripple focus-premium" style={{whiteSpace: 'nowrap'}}>
            Demander un conseil gratuit
          </a>
        </div>
      </div>
    </section>
  );
};

export default Blog;